// Persisted snapshot of the user's last-used recorder settings (APP-6502, FR-006a).
//
// Offline desktop recording is transparent (same record path, no separate UI), so an
// offline session must pre-fill the per-session settings from the user's last-used
// values — which normally live on the server-fetched user object, unavailable on a
// cold offline boot. This atom is the durable copy: written online by
// `useCacheRecorderSettings`, read offline by `buildOfflineCreateArgs`.
//
// Microphone is intentionally NOT here — it already persists as
// `tauriLastUsedInputDeviceAtom` in the persisted preferences. Advanced style knobs and
// the Free/Pro brain are out of scope offline per FR-006a (grey-out is APP-6672).
import { getDefaultStore } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import { getSafeLocalStorage } from '@/lib/jotai-storage';
import type { LanguageCode, TranscribeMode, VoiceStyle } from '@heidi/schema/ml-scribe';

import { createOfflineStorageFailureReporter } from './storage-failure-reporter';

/**
 * The FR-006a "basic settings" snapshot. Most fields are optional — a user who has
 * never set a given control online has no last-used value for it, and the offline
 * create simply omits that arg (the server applies its default on sync). The
 * exception is `outputLanguage` (see its field doc), which always resolves to at
 * least `'en'` to mirror the online recorder.
 *
 * `transcribeMode` is the online recorder's transcribe-vs-dictate toggle; it maps to
 * the offline create arg confusingly named `dictationType` (see `buildOfflineCreateArgs`).
 */
export interface CachedRecorderSettings {
  templateId?: string;
  transcribeMode?: TranscribeMode;
  /** Input/capture language (single — first of the user's input languages). */
  inputLanguage?: LanguageCode;
  /**
   * Output/note language (single — first of the user's output languages). Unlike the
   * other fields this is effectively never omitted: it's derived from
   * `getDefaultDictationOutputLanguages()`, which bakes in an `'en'` fallback on every
   * path, so an unset user gets `'en'` — matching the online recorder's behaviour
   * rather than deferring to the server default.
   */
  outputLanguage?: LanguageCode;
  /** Note style — the online recorder's "Voice" dropdown (backend `voice_style`). */
  voiceStyle?: VoiceStyle;
}

/**
 * Cleared on full logout by `clearCachedLastUsedRecorderSettings()` below. It
 * is unscoped and shared by every account on the device, and the reasoning that
 * used to excuse it — "the next clinician's `/user` response overwrites it" —
 * assumes a network response arrives. This is the OFFLINE feature; the case
 * where it does not arrive is the feature's own domain. On a cold offline boot
 * after a user switch, clinician B's first offline session would otherwise
 * inherit A's template, transcribe mode and languages.
 */
// The string was already here, inline in the atom below; naming it is what the
// sign-out clear needs in order to remove the row without going through the
// atom.
// UNCARVED DOMAIN KEY (offline-recording, DSK-675): offline recording has no
// workspace package, so there is no /contracts door to declare this in. Not a
// query cache identity — a browser-storage key, registered in
// persistence-manifest.json, exactly as the sibling offline keys are.
export const CACHED_LAST_USED_RECORDER_SETTINGS_STORAGE_KEY =
  'heidi.offlineRecording.lastUsedRecorderSettings';

/**
 * Every refused storage OPERATION on this key — a read, a write, or the
 * sign-out removal that throws once the store is in hand — is reported through
 * here. A browser that refuses access to `window.localStorage` itself is a
 * different case the shared wrapper absorbs before any handler is consulted;
 * see the scope note on the reporter module.
 *
 * The safe wrapper still SWALLOWS the throw: a refused write must not break a
 * render, and this key's removal runs partway through `clearLogoutStorage`,
 * where an escaping throw would abort the rest of the sign-out clear. What the
 * reporter adds is that the failure stops being invisible. Silence is the right
 * call for a preference whose later step matters more than its persistence; it
 * is the wrong call here, because `buildOfflineCreateArgs` reads this snapshot
 * back on a cold offline boot, which is the one case where no `/user` response
 * arrives to heal it.
 *
 * Constructed with `getSafeLocalStorage(reporter)` rather than the argument-free
 * `createSafeJotaiLocalStorage` helper — the reporter seam is on the wrapper
 * itself, and threading an optional handler through the helper would reshape a
 * signature shared with every other consumer.
 */
const reportStorageFailure = createOfflineStorageFailureReporter(
  'cachedLastUsedRecorderSettingsAtom',
  'localStorage'
);

export const cachedLastUsedRecorderSettingsAtom =
  atomWithStorage<CachedRecorderSettings | null>(
    CACHED_LAST_USED_RECORDER_SETTINGS_STORAGE_KEY,
    null,
    createJSONStorage<CachedRecorderSettings | null>(() =>
      getSafeLocalStorage(reportStorageFailure)
    ),
    { getOnInit: true }
  );

cachedLastUsedRecorderSettingsAtom.debugLabel = 'cachedLastUsedRecorderSettingsAtom';

/** Non-reactive read for the offline create path (runs inside a mutationFn). */
export const getCachedLastUsedRecorderSettings = (): CachedRecorderSettings | null =>
  getDefaultStore().get(cachedLastUsedRecorderSettingsAtom);

/** Write the snapshot, skipping no-op writes to avoid localStorage churn. */
export const setCachedLastUsedRecorderSettings = (next: CachedRecorderSettings): void => {
  const store = getDefaultStore();
  const current = store.get(cachedLastUsedRecorderSettingsAtom);
  if (current && JSON.stringify(current) === JSON.stringify(next)) {
    return;
  }
  store.set(cachedLastUsedRecorderSettingsAtom, next);
};

/**
 * Drop the cached snapshot. Called from `clearLogoutStorage`.
 *
 * Two writes, because either one on its own is a half-fix:
 *
 * - The atom is emptied, because removing the storage row behind a live storage
 *   atom leaves memory and storage disagreeing. Its only subscription is the
 *   `storage` window event, which does not fire in the tab that made the
 *   change, so the atom would go on serving the departing clinician's snapshot
 *   — and `buildOfflineCreateArgs` reads the ATOM through
 *   `getCachedLastUsedRecorderSettings`, so the reader this key exists for
 *   would never see the removal. It would not even heal on a later reload,
 *   because the next write re-persists what the atom still holds.
 * - The storage row is removed, because emptying the atom writes `null` back
 *   through the adapter, which persists the string `"null"` rather than
 *   clearing the row. So the removal must come SECOND.
 *
 * Both halves go through the SAFE storage wrappers, which swallow a throwing
 * `Storage`. A restricted webview can refuse storage access outright, and this
 * runs partway through `clearLogoutStorage` — an escaping throw here would
 * abort the clear beside it and the whole `LOGOUT_KEYS` loop below, so a
 * sign-out that could not write would stop clearing anything at all. Both also
 * carry the reporter above, so a sign-out that could not clear this key says so
 * instead of passing silently.
 *
 * The key is also added to `LOGOUT_KEYS` as the registry entry every persisted
 * user-derived key is required to have, and as a backstop if this call site is
 * ever dropped. That list is a `localStorage.removeItem` loop, so it can only
 * ever do the second half — a row there on its own would read as coverage
 * while the live snapshot survived.
 *
 * A returning clinician re-fetches their own snapshot from `/user` on the next
 * online session, so the only loss is a cold offline boot immediately after
 * re-signing in — the same trade the sibling offline preferences make.
 */
export const clearCachedLastUsedRecorderSettings = (): void => {
  getDefaultStore().set(cachedLastUsedRecorderSettingsAtom, null);
  getSafeLocalStorage(reportStorageFailure).removeItem(
    CACHED_LAST_USED_RECORDER_SETTINGS_STORAGE_KEY
  );
};
